import { Typography } from '@forethought-technologies/forethought-elements';
import { Box, IconButton, useTheme } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import {
  handleFreeFormInput,
  modalMinimizeWidget,
} from 'actions/workflows-v2/workflowsV2';
import { useHandleWorkflowTriggerOnClick } from 'components/App/hooks';
import { OrgThemedButton } from 'components/OrgThemedButton';
import { PopupModal } from 'components/PopupModal';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectActionToExecAfterPrivacyConsented,
  selectIsPrivacyConsented,
  selectPrivacyConsentConfig,
} from 'reducers/config';

import {
  privacyConsented,
  selectFeatureFlags,
  selectSelectedIntentIndex,
} from 'reducers/workflows';
import { useHandlePromptIntentOptionClicked } from 'utils/handlePromptIntentOptionClicked';
import safeLinkifyHtml from 'utils/safeLinkifyHtml';
import sanitizeHtml from 'utils/sanitizeHtml';

export const PrivacyConsentModal = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const privacyConsentConfig = useAppSelector(selectPrivacyConsentConfig);
  const isPrivacyConsented = useAppSelector(selectIsPrivacyConsented);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const actionToExecAfterPrivacyConsented = useAppSelector(
    selectActionToExecAfterPrivacyConsented,
  );
  const selectedIntentIndex = useAppSelector(selectSelectedIntentIndex);
  const handleWorkflowTrigger = useHandleWorkflowTriggerOnClick({
    triggerFromPrivacyConsent: true,
  });
  const handlePromptIntentOptionClicked = useHandlePromptIntentOptionClicked({
    triggerFromPrivacyConsent: true,
  });
  const isPrivacyConsentEnabled = featureFlags.includes('privacy_consent');

  if (!isPrivacyConsentEnabled || !privacyConsentConfig) {
    return null;
  }

  const {
    call_to_action_label: callToActionLabel,
    privacy_policy: privacyPolicy,
    prompt_header: promptHeader,
    show_when_conversation_starts: showWhenConversationStarts,
  } = privacyConsentConfig;

  if (!showWhenConversationStarts) {
    return null;
  }

  const handlePrivacyConsented = () => {
    if (!actionToExecAfterPrivacyConsented) {
      dispatch(privacyConsented());
      return;
    }

    switch (actionToExecAfterPrivacyConsented) {
      case 'intentOptionClicked':
        // selectedIntentIndex must be defined here because an intent option was clicked,
        // 0 is here to bypass type checking
        handlePromptIntentOptionClicked(selectedIntentIndex || 0);
        break;
      case 'initialIntentTriggered':
        handleWorkflowTrigger();
        break;
      case 'handleFreeFormInput':
        dispatch(privacyConsented());
        dispatch(handleFreeFormInput());
        break;
      default:
        break;
    }
  };

  return (
    <PopupModal
      onClickAway={() => {
        dispatch(modalMinimizeWidget());
      }}
      open={!isPrivacyConsented}
      position='bottom'
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Box
          dir='auto'
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '8px',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='font16Bold'>
            {promptHeader || 'Prompt header'}
          </Typography>
          <Box sx={{ alignSelf: 'flex-start' }}>
            <IconButton
              aria-label='Close privacy consent modal'
              onClick={() => {
                dispatch(modalMinimizeWidget());
              }}
            >
              <IconX color={theme.palette.colors.grey[700]} size={20} />
            </IconButton>
          </Box>
        </Box>
        <Box
          sx={{
            overflowY: 'auto',
            width: '100%',
          }}
        >
          <div
            // eslint-disable-next-line react/forbid-dom-props
            dangerouslySetInnerHTML={{
              __html: safeLinkifyHtml(
                sanitizeHtml(privacyPolicy || 'privacy body'),
              ),
            }}
          ></div>
        </Box>
        <Box sx={{ width: '100%' }}>
          <OrgThemedButton
            fullWidth
            onClick={handlePrivacyConsented}
            variant='contained'
          >
            {callToActionLabel || 'Accept terms'}
          </OrgThemedButton>
        </Box>
      </Box>
    </PopupModal>
  );
};
